import type { TWorkspaceCapacityGroupBy } from "@/types/workspace-capacity";

/**
 * Pixels per day at each zoom. Everything on the canvas is positioned on a
 * continuous day axis (`daysFromStart * DAY_PX`), so month buckets of unequal
 * length size themselves correctly.
 */
export const DAY_PX: Record<TWorkspaceCapacityGroupBy, number> = {
  day: 64,
  week: 20,
  month: 15,
};

export type CapacityScanZoom = Exclude<TWorkspaceCapacityGroupBy, "month">;

/** Days one column covers at each zoom. The arrows page by the whole window
 *  (`visibleDays`), not by one of these. */
export const PERIOD_DAYS: Record<CapacityScanZoom, number> = {
  day: 1,
  week: 7,
};

/**
 * How many columns the visible window holds, per zoom.
 *
 * The count is **measured, not fixed**: the view sizes the timeline band against
 * the viewport and asks for as many columns as fit at
 * {@link SCAN_WEEK_COLUMN_MAX_PX} each (see {@link capacityColumnsForWidth}), so a
 * wide monitor gets more weeks rather than dead space to the right of the grid
 * (Eric, 2026-09-14). The bounds are:
 *
 *  - `min` — the narrowest window that still answers the view's question. Six
 *    weeks was the whole window before it became responsive (decided
 *    2026-09-03); day zoom keeps a full week, since it is the drill-down a week
 *    bar's "Open week" leads to.
 *  - `max` — a payload ceiling, not an API one: the capacity range guard is
 *    `MAX_ALLOCATION_RANGE_DAYS` (560), far above either of these. Half a year of
 *    week buckets, or a month of day buckets, is as much as one screen of people
 *    is worth reading in one read.
 */
export const MIN_COLUMNS: Record<CapacityScanZoom, number> = { day: 7, week: 6 };
export const MAX_COLUMNS: Record<CapacityScanZoom, number> = { day: 31, week: 26 };

/**
 * Scan-first week view geometry. The header, team rows, and person rows share
 * one grid template so their identity, timeline, totals, and action columns
 * always align. The timeline band takes every pixel the three fixed columns do
 * not need, and its columns divide it evenly. Each bucket owns one proportional
 * bar cell.
 *
 * Bars are scaled **proportionally to that week's own working hours**, never at
 * an absolute px-per-hour rate. An absolute rate cannot keep both a 40h week and
 * small booking differences legible inside the bounded container. Proportion
 * also compares load honestly across people: a 20h part-timer with 10h booked
 * and a 40h full-timer with 20h booked both read half full. Absolute hours live
 * in the row's number and the popover.
 */
/** 300px, not 240: grouped by team, two lines at this width hold most real team
 *  names before the ellipsis, where 240 cut "Compliance & Regulatory CoE" to
 *  "Co…" once its pills took the row (NG-2296). */
const SCAN_NAME_COLUMN_PX = 300;
const SCAN_HEADROOM_COLUMN_PX = 118;
const SCAN_ACTION_COLUMN_PX = 96;
export const SCAN_WEEK_COLUMN_MIN_PX = 64;
export const SCAN_WEEK_COLUMN_MAX_PX = 96;
/** `gap-3.5` between the four grid columns, and the rows' `px-4` gutters. */
const SCAN_GRID_GAP_PX = 14;
const SCAN_GRID_PADDING_X_PX = 16;
const SCAN_BAND_MIN_PX = SCAN_WEEK_COLUMN_MIN_PX * MIN_COLUMNS.week;
/** `1fr`, not a max: capping the band is what left the dead space on the right. */
export const SCAN_GRID_TEMPLATE_COLUMNS = `${SCAN_NAME_COLUMN_PX}px minmax(${SCAN_BAND_MIN_PX}px, 1fr) ${SCAN_HEADROOM_COLUMN_PX}px ${SCAN_ACTION_COLUMN_PX}px`;
export const SCAN_ROW_PX = 72;
export const SCAN_CELL_TRACK_TOP_PX = 8;
export const SCAN_CELL_TRACK_HEIGHT_PX = 38;
export const SCAN_CELL_BASELINE_PX = SCAN_CELL_TRACK_TOP_PX + SCAN_CELL_TRACK_HEIGHT_PX;

export function scanBandGridTemplateColumns(columnCount: number): string {
  return `repeat(${columnCount}, minmax(0, 1fr))`;
}

/** Width the timeline band gets inside a grid `gridWidth` px wide. */
export function scanBandWidth(gridWidth: number): number {
  const fixed =
    SCAN_NAME_COLUMN_PX +
    SCAN_HEADROOM_COLUMN_PX +
    SCAN_ACTION_COLUMN_PX +
    SCAN_GRID_GAP_PX * 3 +
    SCAN_GRID_PADDING_X_PX * 2;
  return Math.max(0, gridWidth - fixed);
}

/**
 * How many periods to show in a grid `gridWidth` px wide: as many as fit at the
 * column's *maximum* width, so columns only ever stretch past it once the zoom's
 * ceiling is reached. Clamped to {@link MIN_COLUMNS} / {@link MAX_COLUMNS}, and
 * a width of 0 (an unmeasured container, jsdom) yields the minimum rather than a
 * one-column window.
 */
export function capacityColumnsForWidth(zoom: CapacityScanZoom, gridWidth: number): number {
  const fits = Math.floor(scanBandWidth(gridWidth) / SCAN_WEEK_COLUMN_MAX_PX);
  return Math.min(MAX_COLUMNS[zoom], Math.max(MIN_COLUMNS[zoom], fits));
}

/**
 * Applied to the window-dependent visuals — the week cells, a row's headroom
 * figure, a team's distribution band, the people-by-state strip — while the next
 * window's read is in flight.
 *
 * The same elements, in the same places, pulsed rather than replaced. A skeleton
 * that swaps the content out would change the layout and so reintroduce exactly
 * the flashing the carry-over exists to stop; pulsing the real shapes says
 * "these numbers are being refreshed" without moving anything by a pixel
 * (Aaron, 2026-09-15). `animate-pulse` is the same animation `ui/skeleton` uses,
 * so this reads as the app's own loading language.
 *
 * The view holds it back for {@link SCAN_PENDING_DELAY_MS} so a fast read shows
 * nothing at all — the pulse is for the slow production reads it was asked for.
 */
export const SCAN_PENDING_CLASS = "animate-pulse";
export const SCAN_PENDING_DELAY_MS = 200;
